import * as React from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
  useSearchParams,
  useNavigate,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import routes, { paths, attachedStoreRoutes } from './routes';
import {
  AuthProvider,
  syncAuthUser,
  useAuth,
  verifyToken,
} from './context/authContext';
import getCookie from './utils/getCookie';
import NotFound from './pages/NotFound/NotFound';
import Index from './pages';
import getSubdomain from './utils/getSubdomain';
import getAppURL from './utils/getAppURL';
import Home from './pages/Landing/Home';
import Pricing from './pages/Landing/Pricing';
import Support from './pages/Landing/Support';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function Redirect({ children }: { children: JSX.Element }) {
  const token = getCookie('_pos_tokid');
  const store = getCookie('_pos_store');
  const location = useLocation();
  const subdomain = getSubdomain(window.location.origin);

  if (token && store) {
    return <Navigate to={paths.DASHBOARD_URL_PATH} replace />;
  }

  if (subdomain) {
    // For now any subdomain should be a logged in shop
    const host = window.location.origin.replace(`${subdomain}.`, 'www.');
    window.location.replace(`${host}${location.pathname}`);
  }

  return children;
}

function RequireAuth({
  roles,
  children,
}: {
  children: JSX.Element;
  roles: string[];
}) {
  const [{ user, status }, dispatch] = useAuth();
  const token = getCookie('_pos_tokid');
  const store = getCookie('_pos_store');
  const subdomain = getSubdomain(window.location.origin);

  const location = useLocation();

  React.useEffect(() => {
    if (!user) {
      syncAuthUser(dispatch);
    }
  }, []);

  if (!token && !store) {
    if (subdomain) {
      const host = window.location.origin.replace(`${subdomain}.`, 'www.');
      window.location.replace(`${host}${paths.LOGIN_URL_PATH}`);
    } else {
      return (
        <Navigate
          to={paths.LOGIN_URL_PATH}
          state={{ from: location }}
          replace
        />
      );
    }
  }

  if (status === 'LOADING' || !user) {
    return <div>Please wait...</div>;
  }

  if (roles.length > 0 && !roles.includes(user.role)) {
    return <Navigate to={paths.DASHBOARD_URL_PATH} replace />;
  }

  return children;
}

function StoreFinder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const store = searchParams.get('store');
  const plan = searchParams.get('plan');
  const error = searchParams.get('error');

  if (token && store) {
    verifyToken(
      token,
      store,
      plan,
      (response?: { type: 'RESOLVED' | 'REJECTED'; message: string }) => {
        if (response && response.type === 'REJECTED' && response.message) {
          window.location.replace(
            `${getAppURL()}/login?error=${response.message}`,
          );
          return;
        }

        navigate(paths.DASHBOARD_URL_PATH, {
          replace: true,
          state: response ? { alert: response.message } : {},
        });
      },
    );
  }

  if (error) {
    window.location.replace(`${getAppURL()}/login?error=${error}`);
    return null;
  }

  return (
    <div className="text-[15px]">
      <span>Please wait...</span>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path={paths.LANDING_HOME_URL_PATH} element={<Home />} />
            <Route
              path={paths.LANDING_PRICING_URL_PATH}
              element={<Pricing />}
            />
            <Route
              path={paths.LANDING_SUPPORT_URL_PATH}
              element={<Support />}
            />
            <Route
              path={paths.STORE_FINDER_URL_PATH}
              element={<StoreFinder />}
            />
            <Route
              path={`/:store${paths.STORE_FINDER_URL_PATH}`}
              element={<StoreFinder />}
            />
            {routes
              .filter(route => !route.private)
              .map(route => {
                const Component = route.component;
                return (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={
                      <React.Suspense fallback={<div>Please wait...</div>}>
                        <Redirect>
                          <Component />
                        </Redirect>
                      </React.Suspense>
                    }
                  />
                );
              })}
            {[...routes, ...attachedStoreRoutes]
              .filter(route => route.private)
              .map(route => {
                const Component = route.component;
                return (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={
                      <React.Suspense fallback={<div>Please wait...</div>}>
                        <RequireAuth roles={route.roles || []}>
                          <Index>
                            <Component />
                          </Index>
                        </RequireAuth>
                      </React.Suspense>
                    }
                  />
                );
              })}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
